import { useState } from "react";

type FormData = { name: string; email: string; company: string; jobType: string; description: string; pay: string; urgency: string; workers: string };

const INITIAL: FormData = { name: "", email: "", company: "", jobType: "", description: "", pay: "", urgency: "TODAY", workers: "1" };

export default function PostJobPanel() {
  const [form, setForm] = useState<FormData>(INITIAL);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const set = (key: keyof FormData) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    if (key === "description") setCharCount(e.target.value.length);
    setForm((p) => ({ ...p, [key]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    const data = new URLSearchParams();
    (Object.entries(form) as [string, string][]).forEach(([k, v]) => data.append(k, v));
    try {
      await fetch("https://readdy.ai/api/form/d75cvlivu4krcbn3bki0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
    } finally {
      setLoading(false);
      setSubmitted(true);
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-14">
        <div className="w-16 h-16 flex items-center justify-center bg-emerald-100 rounded-full mx-auto mb-5">
          <i className="ri-check-double-line text-emerald-600 text-3xl" />
        </div>
        <h3 className="text-2xl font-extrabold text-gray-900 mb-2">Job Posted!</h3>
        <p className="text-gray-500 text-sm mb-6 max-w-xs mx-auto">
          We're matching your job with available workers right now. Expect responses within minutes.
        </p>
        <button
          onClick={() => { setSubmitted(false); setForm(INITIAL); setCharCount(0); }}
          className="px-6 py-2.5 bg-emerald-500 text-white font-bold rounded-xl text-sm hover:bg-emerald-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          Post Another Job
        </button>
      </div>
    );
  }

  return (
    <form
      data-readdy-form
      onSubmit={handleSubmit}
      className="flex flex-col gap-5"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-bold text-gray-700 mb-1.5">Your Name</label>
          <input name="name" type="text" required value={form.name} onChange={set("name")}
            className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
            placeholder="John Smith" />
        </div>
        <div>
          <label className="block text-xs font-bold text-gray-700 mb-1.5">Email Address</label>
          <input name="email" type="email" required value={form.email} onChange={set("email")}
            className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
            placeholder="you@company.com" />
        </div>
      </div>

      <div>
        <label className="block text-xs font-bold text-gray-700 mb-1.5">Company / Organization <span className="text-gray-400 font-normal">(optional)</span></label>
        <input name="company" type="text" value={form.company} onChange={set("company")}
          className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
          placeholder="Acme Corp" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-bold text-gray-700 mb-1.5">Job Category</label>
          <select name="jobType" required value={form.jobType} onChange={set("jobType")}
            className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-400 cursor-pointer">
            <option value="">Select category</option>
            {["Landscaping", "Moving / Labor", "Cleaning", "Construction", "Events", "Personal Services", "Driving / Delivery", "Other"].map((c) => (
              <option key={c}>{c}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-bold text-gray-700 mb-1.5">Urgency</label>
          <select name="urgency" required value={form.urgency} onChange={set("urgency")}
            className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-400 cursor-pointer">
            {["URGENT", "TODAY", "TOMORROW", "THIS WEEK", "FLEXIBLE"].map((u) => <option key={u}>{u}</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-bold text-gray-700 mb-1.5">Pay Rate</label>
          <input name="pay" type="text" required value={form.pay} onChange={set("pay")}
            className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
            placeholder="e.g. $25/hr or $200 flat" />
        </div>
        <div>
          <label className="block text-xs font-bold text-gray-700 mb-1.5">Workers Needed</label>
          <select name="workers" required value={form.workers} onChange={set("workers")}
            className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-400 cursor-pointer">
            {["1", "2", "3", "4", "5", "6-10", "10+"].map((n) => <option key={n}>{n}</option>)}
          </select>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-xs font-bold text-gray-700">Job Description</label>
          <span className={`text-xs ${charCount > 450 ? "text-red-500" : "text-gray-400"}`}>{charCount}/500</span>
        </div>
        <textarea name="description" required maxLength={500} rows={4} value={form.description} onChange={set("description")}
          className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-emerald-400"
          placeholder="Describe the job, location, required equipment, and any important details..." />
      </div>

      <button type="submit" disabled={loading}
        className="w-full py-3.5 bg-emerald-500 text-white font-bold rounded-xl text-sm hover:bg-emerald-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60">
        {loading
          ? <span className="flex items-center justify-center gap-2"><i className="ri-loader-4-line animate-spin" />Posting...</span>
          : <><i className="ri-send-plane-line mr-2" />Post Job &amp; Find Workers</>
        }
      </button>
      <p className="text-xs text-center text-gray-400">10–20% platform commission on completed jobs only. No upfront fee.</p>
    </form>
  );
}
